import { useEffect, useRef, useState } from "react";
import { heicTo } from "heic-to";

/**
 * Check if URL contains .heic or .heif before query params
 */
export const isHeicUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  const pathPart = url.split("?")[0] ?? "";
  const lowercasePath = pathPart.toLowerCase();
  return lowercasePath.endsWith(".heic") || lowercasePath.endsWith(".heif");
};

/**
 * Check if a blob is actually a HEIC/HEIF image by checking its magic bytes.
 * HEIC/HEIF files have "ftyp" at bytes 4-7, followed by brand identifier.
 */
export const isActualHeicBlob = async (blob: Blob): Promise<boolean> => {
  const buffer = await blob.slice(0, 12).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Check for "ftyp" at bytes 4-7
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    const brand = String.fromCharCode(bytes[8] ?? 0, bytes[9] ?? 0, bytes[10] ?? 0, bytes[11] ?? 0);
    return ["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1"].includes(brand);
  }

  return false;
};

interface UseHeicImageResult {
  src: string;
  isLoading: boolean;
  error: Error | null;
}

export const useHeicImage = (originalUrl: string | undefined): UseHeicImageResult => {
  const [src, setSrc] = useState<string>(originalUrl || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // Revoke previous object URL if exists
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    if (!originalUrl) {
      setSrc("");
      return;
    }

    // If URL doesn't look like HEIC, use it directly
    if (!isHeicUrl(originalUrl)) {
      setSrc(originalUrl);
      return;
    }

    let isMounted = true;

    const processImage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(originalUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();

        // Check if it's actually a HEIC file (server might have already converted it)
        const isRealHeic = await isActualHeicBlob(blob);

        if (!isRealHeic) {
          // File was already converted server-side, just create object URL from the blob
          if (!isMounted) return;
          const newObjectUrl = URL.createObjectURL(blob);
          objectUrlRef.current = newObjectUrl;
          setSrc(newObjectUrl);
          return;
        }

        // Actually need to convert HEIC to JPEG using heic-to
        const convertedBlob = await heicTo({
          blob,
          type: "image/jpeg",
          quality: 0.85,
        });

        if (!isMounted) return;

        if (convertedBlob instanceof Blob) {
          const newObjectUrl = URL.createObjectURL(convertedBlob);
          objectUrlRef.current = newObjectUrl;
          setSrc(newObjectUrl);
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err : new Error("Failed to process image"));
        // Fallback to original URL
        setSrc(originalUrl);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void processImage();

    return () => {
      isMounted = false;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [originalUrl]);

  return { src, isLoading, error };
};
